import { FooterCommonLink } from '@cat-ecom/pcc-components';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  CVRPromoteEnrollCountryCodes,
  USER_TYPE_GUEST
} from '@app/constants/commonConstants';
import links from '@app/constants/links';
import { replaceTokensInString, normalizeUrl } from '@app/utils';
import { useSelector } from 'react-redux';
import { useControlCenterChecker } from '@app/components/pages/account/hooks';
import useControlCenterUrl from '@app/hooks/useControlCenterUrl';
import useShoppingCartFeatureFlag from '@app/hooks/useShoppingCartFeatureFlag';
import useSavedListsFeatureFlag from '@app/hooks/useSavedListsFeatureFlag';
import useOrderHistoryFeatureFlag from '@app/hooks/useOrderHistoryFeatureFlag';
import useCatVantageEnrollmentLink from '@app/hooks/useCatVantageEnrollmentLink';
import { PCC_RELEASE_CAT_FI_ENHANCEMENTS_PHASE_3 } from '@app/constants/featureFlags';

const FooterAccountLinks = ({ isFooterDesignUpdate }) => {
  const { t } = useTranslation();
  const storeId = useSelector(state => state?.common?.storeId);
  const langId = useSelector(state => state?.common?.langId);
  const catalogId = useSelector(state => state?.common?.catalogId);
  const userType = useSelector(state => state?.common?.userType);
  const isCatCSR = useSelector(s => s.common?.isCatCSR);
  const isBuyOnBehalf = useSelector(s => s.common.isBuyOnBehalf);
  const { isControlCenterEnabled } = useControlCenterChecker();
  const isUserRegistered = userType !== USER_TYPE_GUEST;

  const { MA_SUMMARY_URL, SHOPPING_CART_LINK } = links;

  const isCatVantageRewardsEnabled = useSelector(
    state => state.featureFlag.PCC_CatVantageRewardsFlag
  );
  const isCSPCustomer = useSelector(s => s.common?.isCSPCustomer);
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const isCVRImproveVisibilityEnabled = useSelector(
    state => state.featureFlag.PCC_CVRImproveVisibility
  );

  const isCatFiPhase3Enabled = useSelector(
    s => s.featureFlag?.[PCC_RELEASE_CAT_FI_ENHANCEMENTS_PHASE_3]
  );
  const countryCode = window?.catReact?.akamaiHeaderCountryCode;

  const getLinkUrl = url => {
    const link = replaceTokensInString(url, catalogId, langId, storeId);
    return normalizeUrl(link);
  };
  const controlCenterUrl = useControlCenterUrl();
  const shoppingCartUrl = useShoppingCartFeatureFlag();
  const { redirectUrl: savedListsUrl } = useSavedListsFeatureFlag();
  const { redirectUrl: orderHistoryUrl } = useOrderHistoryFeatureFlag();

  const buildCartUrl = replaceTokensInString(
    SHOPPING_CART_LINK,
    storeId,
    langId,
    catalogId
  );

  const savedListRedirection =
    isCatCSR && !isBuyOnBehalf ? buildCartUrl : savedListsUrl;

  const legacyShowCVREnrollment =
    isCVRImproveVisibilityEnabled &&
    (isCatVantageRewardsEnabled
      ? !isUserRegistered || isCSPCustomer
      : isCatCorp);

  const CVRCountryCheck = CVRPromoteEnrollCountryCodes.includes(countryCode);

  const enhancedShowCVREnrollment =
    (!isUserRegistered && CVRCountryCheck) ||
    (isCSPCustomer && isCatVantageRewardsEnabled);

  const showCVREnrollment = isCatFiPhase3Enabled
    ? enhancedShowCVREnrollment
    : legacyShowCVREnrollment;

  const catVantageEnrollmentLink = useCatVantageEnrollmentLink();

  const getAccountLink = () => (
    <li>
      {isControlCenterEnabled ? (
        <FooterCommonLink
          isMobile={isFooterDesignUpdate}
          label={t('CONTROL_CENTER')}
          href={controlCenterUrl}
        />
      ) : (
        <FooterCommonLink
          isMobile={isFooterDesignUpdate}
          label={t('SUMMARY')}
          href={getLinkUrl(MA_SUMMARY_URL)}
        />
      )}
    </li>
  );

  return (
    <>
      {isUserRegistered && getAccountLink()}
      <li>
        <FooterCommonLink
          isMobile={isFooterDesignUpdate}
          label={t('MINI_SHOPCART_SHOPPING_CART')}
          href={shoppingCartUrl}
        />
      </li>
      {isUserRegistered && (
        <li>
          <FooterCommonLink
            isMobile={isFooterDesignUpdate}
            label={t('MA_ORDER_HISTORY')}
            href={orderHistoryUrl}
          />
        </li>
      )}
      {isUserRegistered && (
        <li>
          <FooterCommonLink
            isMobile={isFooterDesignUpdate}
            label={t('SAVED_LISTS')}
            href={savedListRedirection}
          />
        </li>
      )}
      {showCVREnrollment && (
        <li>
          <FooterCommonLink
            isMobile={isFooterDesignUpdate}
            label={'Cat Vantage Rewards'}
            href={catVantageEnrollmentLink}
          />
        </li>
      )}
    </>
  );
};

export default FooterAccountLinks;
FooterAccountLinks.propTypes = {
  isFooterDesignUpdate: PropTypes.bool
};
FooterAccountLinks.defaultProps = {
  isFooterDesignUpdate: false
};
